import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2, Lock, Shield } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [error, setError] = useState("");
  const [authConfig, setAuthConfig] = useState(null);

  // Set page title
  useEffect(() => {
    document.title = "DHI | Admin Command Center";
  }, []);

  // Check auth status and handle OAuth callback
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check if already logged in
        const token = localStorage.getItem("adminToken");
        if (token) {
          // Verify token is still valid
          try {
            await axios.get(`${API}/admin/me`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            // Token valid, redirect to dashboard
            navigate("/admin");
            return;
          } catch {
            // Token invalid, clear it
            localStorage.removeItem("adminToken");
          }
        }

        // Check for OAuth callback code
        const code = searchParams.get("code");
        if (code) {
          setLoading(true);
          try {
            const response = await axios.post(`${API}/admin/auth/callback`, { code });
            if (response.data.token) {
              localStorage.setItem("adminToken", response.data.token);
              const state = searchParams.get("state");
              navigate(state || "/admin");
              return;
            }
          } catch (err) {
            setError("Microsoft authentication failed. Please try again.");
          }
          setLoading(false);
        }

        // Get auth configuration
        const configResponse = await axios.get(`${API}/admin/auth/config`);
        setAuthConfig(configResponse.data);
      } catch (err) {
        console.error("Auth check failed:", err);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
  }, [navigate, searchParams]);

  // Handle Microsoft SSO Login
  const handleMicrosoftLogin = () => {
    if (!authConfig?.azureConfigured) {
      setError("Azure AD SSO is not configured. Please use development login.");
      return;
    }

    const { clientId, tenantId } = authConfig;
    // Use dynamic redirect URI based on current origin for proper OAuth across environments
    const dynamicRedirectUri = window.location.origin + '/admin/login';
    const scope = encodeURIComponent("openid profile email");
    const state = encodeURIComponent("/admin");
    
    const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=code&` +
      `redirect_uri=${encodeURIComponent(dynamicRedirectUri)}&` +
      `scope=${scope}&` +
      `state=${state}&` +
      `prompt=select_account`;
    
    window.location.href = authUrl;
  };

  // Development mode login
  const handleDevLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Validate email domain for production-like behavior
      if (email && !email.toLowerCase().endsWith("@dhihospitality.com")) {
        setError("Access restricted to @dhihospitality.com accounts only.");
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/admin/auth/dev-token`, { 
        email: email || "admin@dhihospitality.com" 
      });
      
      if (response.data.token) {
        localStorage.setItem("adminToken", response.data.token);
        navigate("/admin");
      }
    } catch (err) {
      console.error("Login failed:", err);
      setError(err.response?.data?.message || "Failed to authenticate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1C2470] to-[#141A52]">
        <Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1C2470] to-[#141A52] p-4"
      data-testid="admin-login"
    >
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="h-8 w-8 text-[#C5A059]" />
          </div>
          <CardTitle 
            className="text-2xl font-bold text-[#1C2470]" 
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Admin Command Center
          </CardTitle>
          <CardDescription>
            dhi Hospitality • Restricted Access
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Microsoft SSO Button (Primary when configured) */}
          {authConfig?.azureConfigured ? (
            <div className="space-y-4">
              <Button
                onClick={handleMicrosoftLogin}
                className="w-full h-12 bg-[#1C2470] hover:bg-[#2A3580] text-white"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 21 21" fill="currentColor">
                    <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
                  </svg>
                )}
                Sign in with Microsoft
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Restricted to @dhihospitality.com accounts
              </p>
            </div>
          ) : (
            /* Development Mode Login */
            <form onSubmit={handleDevLogin} className="space-y-4" data-testid="login-form">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 flex items-center gap-2">
                  <Lock className="h-3 w-3" />
                  Development Mode - Azure AD SSO not configured
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@dhihospitality.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="email-input"
                />
                <p className="text-xs text-muted-foreground">
                  Use @dhihospitality.com domain for access
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-[#1C2470] hover:bg-[#2A3580]"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Authenticating...
                  </>
                ) : (
                  "Access Dashboard"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
